import { Link } from 'react-router-dom'
import { imageUrl } from '../lib/images'

export default function Footer({ settings }) {
  const currentYear = new Date().getFullYear()

  const bookingUrl = settings?.booking_url || 'https://service.dinbilpartner.dk/book-tid'
  const phone = settings?.phone || '22 18 73 30'
  const phoneRaw = settings?.phone_raw || '22187330'
  const email = settings?.email || 'steen@ekbiler.dk'
  const address = settings?.address || 'Mejerivej 12, 7250 Hejnsvig'
  const cvr = settings?.cvr || '28566956'
  const facebookUrl = settings?.facebook_url || 'https://www.facebook.com/ekbiler.dk/'
  const mapsUrl =
    settings?.maps_url ||
    `https://www.google.com/maps/search/?api=1&query=EK+Biler+${encodeURIComponent(address)}`

  return (
    <footer className="bg-[#14171a] text-gray-300 border-t border-[#262c33]">
      {/* Upper CTA Strip */}
      <div className="bg-[#1a1f24] border-b border-[#262c33] py-8 px-4 sm:px-6">
        <div className="max-w-7xl mx-auto flex flex-col lg:flex-row items-center justify-between gap-6">
          <div className="text-center lg:text-left">
            <h3 className="font-display text-2xl sm:text-3xl font-bold uppercase text-white tracking-wide">
              Klar til at bestille tid til værkstedet?
            </h3>
            <p className="text-sm text-gray-400 mt-1">
              Hurtig online tidsbestilling eller ring direkte til vores værksted i Hejnsvig.
            </p>
          </div>
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-center gap-3 w-full sm:w-auto">
            <a
              href={`tel:${phoneRaw}`}
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-6 py-3.5 border border-gray-600 bg-[#222830] text-white text-xs sm:text-sm font-bold uppercase tracking-wider hover:bg-[#2c333e] hover:border-gray-500 transition-colors whitespace-nowrap focus:outline-none focus:ring-2 focus:ring-red-500 text-center"
            >
              <i className="fa-solid fa-phone text-[#e20019]" aria-hidden="true" />
              <span className="whitespace-nowrap">Ring 22 18 73 30</span>
            </a>
            <a
              href={bookingUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-7 py-3.5 bg-[#e20019] hover:bg-[#b80014] text-white text-xs sm:text-sm font-bold uppercase tracking-wider shadow-md hover:shadow-lg transition-all whitespace-nowrap focus:outline-none focus:ring-2 focus:ring-red-500 text-center"
            >
              <i className="fa-solid fa-calendar-check" aria-hidden="true" />
              <span>Book tid online</span>
            </a>
          </div>
        </div>
      </div>

      {/* Main Footer Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-14">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10">
          {/* Column 1: Company Profile */}
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <img
                src={imageUrl('ekbiler-logo.png', 'width=300,quality=90,format=auto')}
                alt="EK Biler logo"
                className="h-10 w-auto object-contain brightness-110"
              />
            </div>
            <p className="text-sm text-gray-400 leading-relaxed">
              EK Biler er et bilfirma, som handler med gode, brugte kvalitetsbiler i forskellige
              størrelser, og udfører professionel bilservice og reparation på eget autoværksted i
              Hejnsvig.
            </p>

            <div className="pt-2">
              <a
                href="https://dinbilpartner.dk/"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block bg-white p-2 rounded-lg border border-gray-700 hover:border-gray-500 transition-colors"
                title="Besøg Din Bilpartner"
              >
                <img
                  src={imageUrl('din-bil-partner-logo.jpg', 'width=320,quality=90,format=auto')}
                  alt="Din Bilpartner medlemslogo"
                  className="h-8 w-auto object-contain"
                />
              </a>
              <div className="text-xs text-gray-400 mt-1.5">
                Stolt medlem af Din Bilpartner siden 2023
              </div>
            </div>
          </div>

          {/* Column 2: Workshop Services */}
          <div>
            <h4 className="text-white font-bold text-sm uppercase tracking-wider mb-4 border-l-2 border-[#e20019] pl-2.5">
              Værkstedsydelser
            </h4>
            <ul className="space-y-2.5 text-sm">
              <li>
                <Link to="/serviceeftersyn" className="hover:text-white transition-colors">
                  Serviceeftersyn
                </Link>
              </li>
              <li>
                <Link to="/daekhotel" className="hover:text-white transition-colors">
                  Dækhotel &amp; opbevaring
                </Link>
              </li>
              <li>
                <Link to="/daekskift" className="hover:text-white transition-colors">
                  Dækskift
                </Link>
              </li>
              <li>
                <Link to="/elbil-service" className="hover:text-white transition-colors">
                  Elbil &amp; hybrid service
                </Link>
              </li>
              <li>
                <Link to="/vinterdaek" className="hover:text-white transition-colors">
                  Montering af vinterdæk
                </Link>
              </li>
              <li>
                <Link to="/autovaerksted-billund" className="hover:text-white transition-colors">
                  Autoværksted nær Billund
                </Link>
              </li>
              <li>
                <Link to="/autovaerksted-grindsted" className="hover:text-white transition-colors">
                  Autoværksted nær Grindsted
                </Link>
              </li>
            </ul>
          </div>

          {/* Column 3: Bilsalg & Hurtige links */}
          <div>
            <h4 className="text-white font-bold text-sm uppercase tracking-wider mb-4 border-l-2 border-[#e20019] pl-2.5">
              Bilsalg &amp; Info
            </h4>
            <ul className="space-y-2.5 text-sm">
              <li>
                <Link to="/brugte-biler" className="hover:text-white transition-colors">
                  Brugte biler til salg
                </Link>
              </li>
              <li>
                <Link to="/finansering" className="hover:text-white transition-colors">
                  Finansiering &amp; Motorforsikring
                </Link>
              </li>
              <li>
                <a
                  href={settings?.indicata_url || 'https://lg.indicata.com/#/?dealerId=156f3c15-a501-4045-bf19-5598ec416fd9'}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:text-white transition-colors flex items-center gap-1.5"
                >
                  <span>Beregn byttepris online</span>
                  <i className="fa-solid fa-arrow-up-right-from-square text-xs text-gray-500" aria-hidden="true" />
                </a>
              </li>
              <li>
                <Link to="/om-os" className="hover:text-white transition-colors">
                  Om os &amp; medarbejdere
                </Link>
              </li>
              <li>
                <a
                  href={facebookUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-400 hover:text-blue-300 transition-colors flex items-center gap-1.5 font-semibold"
                >
                  <i className="fa-brands fa-facebook" aria-hidden="true" />
                  <span>Facebook @ekbiler.dk</span>
                </a>
              </li>
            </ul>
          </div>

          {/* Column 4: Contact & Opening hours */}
          <div>
            <h4 className="text-white font-bold text-sm uppercase tracking-wider mb-4 border-l-2 border-[#e20019] pl-2.5">
              Kontakt &amp; Åbningstider
            </h4>

            <div className="space-y-2.5 text-sm text-gray-300">
              <div className="font-semibold text-white">EK Biler v/Steen Svensson Ek</div>
              <div className="flex items-start gap-2">
                <i className="fa-solid fa-location-dot text-[#e20019] mt-1 shrink-0" aria-hidden="true" />
                <div>
                  <div>{address}</div>
                  <a
                    href={mapsUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[#e20019] hover:underline text-xs font-semibold inline-flex items-center gap-1 mt-0.5"
                  >
                    <span>Find vej på Google Maps</span>
                    <i className="fa-solid fa-arrow-up-right-from-square text-[10px]" aria-hidden="true" />
                  </a>
                </div>
              </div>

              <div className="flex items-center gap-2 pt-1">
                <i className="fa-solid fa-phone text-[#e20019] shrink-0" aria-hidden="true" />
                <a
                  href={`tel:${phoneRaw}`}
                  className="text-white font-semibold hover:text-[#e20019] transition-colors whitespace-nowrap"
                >
                  <span className="whitespace-nowrap">{phone}</span>
                </a>
              </div>

              <div className="flex items-center gap-2">
                <i className="fa-solid fa-envelope text-gray-400 shrink-0" aria-hidden="true" />
                <a
                  href={`mailto:${email}`}
                  className="hover:text-white transition-colors whitespace-nowrap"
                >
                  <span className="whitespace-nowrap">{email}</span>
                </a>
              </div>

              <div className="text-xs text-gray-400 pt-1">
                CVR nr.: <span className="text-gray-200">{cvr}</span>
              </div>

              <div className="pt-3 border-t border-gray-800 text-xs text-gray-400 space-y-1">
                <div className="text-gray-200 font-bold uppercase tracking-wider text-[11px]">
                  Værkstedets åbningstider:
                </div>
                <div>Mandag – Torsdag: 7:30 – 16:00</div>
                <div>Fredag: 7:30 – 13:30</div>
                <div>Lørdag – Søndag: Lukket</div>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="mt-12 pt-8 border-t border-[#262c33] flex flex-col sm:flex-row items-center justify-between gap-4 text-xs text-gray-400">
          <div>
            &copy; {currentYear} EK Biler v/Steen Svensson Ek • Mejerivej 12, 7250 Hejnsvig • CVR: {cvr}
          </div>
          <div className="flex flex-wrap items-center justify-center sm:justify-end gap-x-4 gap-y-2">
            <a
              href="https://www.iubenda.com/privacy-policy/40506532"
              className="iubenda-white iubenda-noiframe iubenda-embed hover:text-white transition-colors"
              title="Privatlivspolitik "
            >
              Privatlivspolitik
            </a>
            <span>•</span>
            <a
              href="https://www.iubenda.com/privacy-policy/40506532/cookie-policy"
              className="iubenda-white iubenda-noiframe iubenda-embed hover:text-white transition-colors"
              title="Cookiepolitik "
            >
              Cookiepolitik
            </a>
            <span>•</span>
            <Link to="/kontakt" className="hover:text-white transition-colors">
              Kontakt
            </Link>
            <span>•</span>
            <a
              href={bookingUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-[#e20019] font-bold hover:underline"
            >
              Book tid
            </a>
            <span>•</span>
            <a
              href={facebookUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-white transition-colors"
            >
              Facebook
            </a>
          </div>
        </div>
      </div>
    </footer>
  )
}
